import { ColDef, ColGroupDef, IRowNode } from 'ag-grid-community';
import { t } from 'i18next';
import {
    ORG_CHART_HR_MAPPING,
    RECURRENCE,
    RETIREMENT_SYSTEM,
    INDICIAIRE,
    MANTY_STATUS,
    JOB_LABEL,
    JOB_NUMBER,
    ORG_CHART_HR_NAME,
} from 'routes/HumanResourcesModule/business/definitions';
import { TEXT_FILTER } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/defaultColDefs';
import {
    agentCellRendererSelectorGetter,
    getGroupedAgentColumn,
} from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/agentColumnHelpers';
import {
    hrPositionMappingValueGetter,
    HR_POSITION_MAPPING_VALUE_GETTER_FIELD,
    recurrentFormatter,
} from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/gridHelpers';
import {
    createLoadedSimulatedColumn,
    createRealizedColumn,
    getAmountColumns,
} from 'routes/HumanResourcesModule/Simulation/helpers/gridHelpers';
import { PAY_COMPONENT } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import { getGroupedOrgChartHrColumn } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';
import { SemiTreatmentHypothesis } from 'routes/HumanResourcesModule/Simulation/helpers/hooks/semiTreatment';
import { sortPayComponents } from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/PayComponentSalaryMass/helpers/dataHelpers';
import { DateString } from 'routes/HumanResourcesModule/business/types';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import {
    createRealizedResultEmployerCostsColumn,
    createPrevisionalSimulatedResultColumn,
    createRealizedEmployerCostsColumn,
    createBudgetPrevisionalSimulatedResultColumn,
    createRealizedResultColumn,
    createSimulatedEmployerCostsColumn,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/helpers/gridHelpers';
import { currencyFormatter } from 'helpers/formatHelper';
import { HrNature } from './hooks/natures';
import { PREVISIONAL_SIMULATED } from './constants';
import { SPLIT_BY } from 'routes/BudgetModule/config/constants';
import { NatureVentilationType } from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/helpers/hrVizInfos';

const getRowGroup = ({
    groupBy,
    field,
}: {
    groupBy: string;
    field: string;
}) => ({
    rowGroup: groupBy === field,
    rowGroupIndex: groupBy === field ? 0 : undefined,
});

export const getColDefs = ({
    groupBy,
    isMonthly,
    dateColumns,
    semiTreatmentHypothesesData,
    hrSimulation,
}: {
    groupBy: string;
    isMonthly: boolean;
    dateColumns: DateString[];
    semiTreatmentHypothesesData: SemiTreatmentHypothesis[];
    hrSimulation: HrSimulation;
}): (ColDef | ColGroupDef)[] => {
    const betweenCurrentAndSimulatedDateColumnGetters = isMonthly
        ? [
              createRealizedResultColumn,
              createRealizedResultEmployerCostsColumn,
              createLoadedSimulatedColumn,
              createSimulatedEmployerCostsColumn,
          ]
        : [createRealizedColumn, createPrevisionalSimulatedResultColumn];
    const pastDateColumnGetters = isMonthly
        ? [createRealizedColumn, createRealizedEmployerCostsColumn]
        : [createRealizedColumn];

    const simulatedDateColumnGetters = isMonthly
        ? [createLoadedSimulatedColumn, createSimulatedEmployerCostsColumn]
        : [createRealizedColumn, createBudgetPrevisionalSimulatedResultColumn];

    return [
        {
            ...getGroupedOrgChartHrColumn(),
            ...getRowGroup({
                field: ORG_CHART_HR_MAPPING,
                groupBy:
                    groupBy === ORG_CHART_HR_NAME
                        ? ORG_CHART_HR_MAPPING
                        : groupBy,
            }),
        },
        {
            ...getGroupedAgentColumn(
                true,
                agentCellRendererSelectorGetter(
                    semiTreatmentHypothesesData,
                    true,
                    hrSimulation.recurrentInitialisationStartDate,
                ),
            ),
            // Agent is always the second row Group
            rowGroupIndex: 1,
        },
        {
            ...TEXT_FILTER,
            headerName: t(`humanResources.simulation.payComponent`),
            field: PAY_COMPONENT,
            enableRowGroup: true,
            pinned: 'left',
            sort: 'asc',
            comparator: sortPayComponents,
            ...getRowGroup({ field: PAY_COMPONENT, groupBy }),
        },
        {
            ...TEXT_FILTER,
            headerName: t('humanResources.settings.recurrence'),
            field: RECURRENCE,
            enableRowGroup: true,
            hide: true,
            valueFormatter: recurrentFormatter,
            filterValueGetter: recurrentFormatter,
            ...getRowGroup({ field: RECURRENCE, groupBy }),
        },
        ...[JOB_LABEL, JOB_NUMBER].map((field) => ({
            ...TEXT_FILTER,
            headerName: t(`humanResources.simulation.${field}`),
            field,
            enableRowGroup: true,
            hide: true,
        })),
        ...[INDICIAIRE, RETIREMENT_SYSTEM, MANTY_STATUS].map(
            (field: HR_POSITION_MAPPING_VALUE_GETTER_FIELD) => ({
                ...TEXT_FILTER,
                headerName: t(`humanResources.settings.${field}`),
                field,
                enableRowGroup: true,
                hide: true,
                filterValueGetter: hrPositionMappingValueGetter(field),
                valueGetter: hrPositionMappingValueGetter(field),
                ...getRowGroup({ field, groupBy }),
            }),
        ),
        ...getAmountColumns({
            simulationStart: hrSimulation.startDate,
            simulationEnd: hrSimulation.endDate,
            betweenCurrentAndSimulatedDateColumnGetters,
            pastDateColumnGetters,
            simulatedDateColumnGetters,
            isMonthly,
            dateColumns,
        }),
    ];
};

export const autoGroupColumnDef = (
    ventilationType: NatureVentilationType,
): ColDef => {
    return {
        cellRendererParams: {
            suppressCount: true,
        },
        headerName:
            ventilationType === NatureVentilationType.NATURE
                ? t('humanResources.simulation.resultByNature.title')
                : t(
                      'humanResources.simulation.resultByNature.titleWithService',
                  ),
        valueFormatter: (params) => params.data?.label,
        filterValueGetter: (params) => params.data?.label,
    };
};

const getServiceFromNode = (node: IRowNode | null): string | null => {
    if (node === null) {
        return null;
    }
    return node.parent?.id === 'ROOT_NODE_ID'
        ? node.key
        : getServiceFromNode(node.parent);
};

export const getNatureColDefs = ({
    hrNatures,
    hrSimulation,
    ventilationType,
}: {
    hrNatures: HrNature[];
    hrSimulation: HrSimulation;
    ventilationType: NatureVentilationType;
}): (ColDef | ColGroupDef)[] => {
    const startYear = new Date(hrSimulation.startDate).getFullYear();
    const endYear = new Date(hrSimulation.endDate).getFullYear();
    const yearsOfSimulation =
        startYear === endYear ? [startYear] : [startYear, endYear];

    const servicesColumns: ColDef[] =
        ventilationType === NatureVentilationType.SERVICE_AND_NATURE
            ? [
                  {
                      headerName: 'Services',
                      field: 'orgChartHr',
                      hide: true,
                      valueGetter: (params) => getServiceFromNode(params?.node),
                  },
              ]
            : [];

    return [
        ...servicesColumns,
        {
            headerName: t(
                'humanResources.simulation.resultByNature.natureLabel',
            ),
            field: 'label',
            valueGetter: (params) => {
                const natureCode = params?.node
                    ? params?.node?.key
                    : params?.data?.natureCode;
                return (
                    hrNatures.find((nature) => nature.code === natureCode)
                        ?.label ?? t('humanResources.simulation.otherNature')
                );
            },
        },
        ...yearsOfSimulation.map(
            (year): ColDef => ({
                headerName:
                    t('humanResources.simulation.resultByNature.amount') +
                    ' ' +
                    year,
                valueFormatter: (params) => currencyFormatter(params.value),
                aggFunc: 'sum',
                valueGetter: (params) => {
                    // In this value getter we are returning the sum of the simulated amounts
                    const row = params?.data;
                    return (
                        row?.[
                            `${PREVISIONAL_SIMULATED}${SPLIT_BY}${year}0101`
                        ] ?? 0
                    );
                },
            }),
        ),
    ];
};
